import React from 'react';
import { money, today, workflowColour } from '../lib/format';
import type { Property, Task } from '../lib/types';
import { Stat, TaskRow, type Ctx } from '../main';

/**
 * Opens on decisions, not dashboards.
 *
 * A manager reaching for this on a phone is asking "is anything going wrong, and
 * can I unblock it from here?" — so what is blocked comes first, each with an
 * action attached, and the counts come after.
 */
export function Today({ ctx, tasks, properties }: {
  ctx: Ctx; tasks: Task[]; properties: Property[];
}): React.JSX.Element {
  const now = today();
  const late = tasks.filter((t) => t.dueDate < now).sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const sos = tasks.filter((t) => t.severity === 'Critical');
  const dueToday = tasks.filter((t) => t.dueDate === now);
  const lateMoney = late.filter((t) => t.type !== 'Action').reduce((a, b) => a + b.amount, 0);
  const rooms = properties.reduce((a, b) => a + b.roomCount, 0);
  const occupied = properties.reduce((a, b) => a + b.occupiedCount, 0);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const byWorkflow: Record<string, number> = {};
  for (const t of tasks) byWorkflow[t.workflow] = (byWorkflow[t.workflow] ?? 0) + 1;
  const busiest = Math.max(...Object.values(byWorkflow), 1);

  return (
    <>
      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22 }}>{greeting}, {ctx.me.name.split(' ')[0]}</h1>
          <div className="sub">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
      </div>

      <div className="bar w">
        <button className="btn sm danger" onClick={() => ctx.newTask({ emergency: true })}>Report an emergency</button>
        <button className="btn sm" onClick={() => ctx.newTask()}>New task</button>
      </div>

      {sos.length > 0 && (
        <div className="alert bad">
          <div className="t">{sos.length === 1 ? 'A critical emergency needs a decision' : `${sos.length} critical emergencies need a decision`}</div>
          <div className="s">{sos[0]!.description || sos[0]!.name} — {ctx.propertyName(sos[0]!.propertyId)}</div>
          <div className="go"><button className="btn" onClick={() => ctx.open(sos[0]!)}>Open it</button></div>
        </div>
      )}

      {lateMoney > 0 && ctx.canManage && (
        <div className="alert warn">
          <div className="t">{money(lateMoney)} tied up in late money tasks</div>
          <div className="s">Costs and rent that should have settled by now.</div>
          <div className="go"><button className="btn" onClick={() => ctx.go('finance')}>See them</button></div>
        </div>
      )}

      <div className="grid g4">
        <Stat label="Overdue" value={late.length} tone={late.length ? 'hot' : undefined}
          foot={late.length ? 'Worst first, below' : 'Nothing is late'} />
        <Stat label="Due today" value={dueToday.length} />
        <Stat label="Open work" value={tasks.length} foot={`Across ${properties.length} sites`} />
        <Stat label="Occupancy" value={rooms ? `${Math.round((occupied / rooms) * 100)}%` : '—'}
          tone="good" foot={`${occupied} of ${rooms} rooms let`} />
      </div>

      {late.length > 0 && (
        <>
          <h2 className="sec">Overdue, worst first</h2>
          {late.slice(0, 6).map((t) => <TaskRow key={t.id} task={t} ctx={ctx} />)}
          {late.length > 6 && (
            <button className="btn" onClick={() => ctx.go('tasks')}>
              See all {late.length} overdue
            </button>
          )}
        </>
      )}

      {late.length === 0 && dueToday.length > 0 && (
        <>
          <h2 className="sec">Due today</h2>
          {dueToday.map((t) => <TaskRow key={t.id} task={t} ctx={ctx} />)}
        </>
      )}

      {tasks.length === 0 && (
        <div className="note ok" style={{ marginTop: 18 }}>
          No open work at all. Either everything is genuinely done, or no workflow has been started yet.
        </div>
      )}

      <h2 className="sec">Open work by workflow</h2>
      <div className="card"><div className="bd">
        {Object.entries(byWorkflow).sort((a, b) => b[1] - a[1]).map(([workflow, count]) => (
          <div key={workflow} style={{ marginBottom: 11 }}>
            <div className="between" style={{ fontSize: 12.5 }}>
              <span style={{ color: workflowColour(workflow), fontWeight: 600 }}>{workflow}</span>
              <b className="mono">{count}</b>
            </div>
            <div className="wfbar">
              <i style={{ width: `${(count / busiest) * 100}%`, background: workflowColour(workflow) }} />
            </div>
          </div>
        ))}
        {Object.keys(byWorkflow).length === 0 && <div className="sub">Nothing open.</div>}
      </div></div>

      <h2 className="sec onlyMobile">Elsewhere</h2>
      <div className="onlyMobile">
        {([['dashboard', 'Dashboard'], ['timeline', 'Timeline'], ['properties', 'Properties'],
           ['security', 'Security cameras'], ['leases', 'Leases and the rent cycle'], ['tenants', 'Tenants'],
           ['projections', 'Projections'], ['workflows', 'Workflow map']] as const)
          .filter(([key]) => ctx.canManage || (key !== 'dashboard' && key !== 'projections')).map(([key, label]) => (
          <button key={key} className="rowcard" onClick={() => ctx.go(key)}>
            <span className="body"><span className="tt">{label}</span></span>
            <span className="sub">›</span>
          </button>
        ))}
      </div>
    </>
  );
}
